import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'react-query';
import { gamingAPI, gpuAPI, paymentsAPI, usersAPI } from '../services/api';
import { Gamepad2, Square } from 'lucide-react';
import StreamingInterface from '../components/StreamingInterface';
import AutomatedGaming from '../components/AutomatedGaming';
// import GPUVerificationStatus from '../components/GPUVerificationStatus'; // Removed - using VMStatusDashboard instead
// import RealtimeGPULogs from '../components/RealtimeGPULogs';
// import RealtimeGPUUtilization from '../components/RealtimeGPUUtilization'; // Removed - simplified UI
import VMStatusDashboard from '../components/VMStatusDashboard';
import RealtimeLogStream from '../components/RealtimeLogStream';
// ParsecClient demoted for V1 — Wolf + Moonlight + Tailscale only
import GPUSelection from '../components/GPUSelection';
import { useAuth } from '../contexts/AuthContext';
import { useStatus } from '../contexts/StatusContext';
import GameConnectPanel from '../components/GameConnectPanel';
import PlayLobbyPanel from '../components/PlayLobbyPanel';
import IdleDestroyToggle from '../components/IdleDestroyToggle';
import SteamPersistToggle from '../components/SteamPersistToggle';
import { fetchBootProgress } from '../utils/bootProgressQuery';

const formatSessionHours = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return '0m';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs <= 0) return `${mins}m`;
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const formatSessionCost = (cost, rate) => {
  const n = Number(cost);
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) return '—';
  if (!Number.isFinite(n) || n <= 0) return '0.00 MKD';
  return `${n.toFixed(2)} MKD`;
};

const Gaming = ({ mode = 'game' }) => {
  const isGame = mode === 'game';
  const pageTitle = isGame ? 'Game' : 'Lab';
  const { user, loading: authLoading } = useAuth();
  const { showSuccess, showError } = useStatus();
  const pageSubtitle = isGame
    ? (user?.family_quad
      ? 'One GPU for the house. Four people at once only in the Moonlight app — or one in the browser plus three in Moonlight.'
      : 'One GPU. Play in the browser or in Moonlight — same machine.')
    : 'Docker instances for private AI and custom workloads';
  // Removed selectedGPU state - no pre-selection, customers choose directly
  const [sessionName, setSessionName] = useState('');
  const [gameTitle, setGameTitle] = useState('');
  const [showStreamingInterface, setShowStreamingInterface] = useState(false);
  const [showAutomatedGaming, setShowAutomatedGaming] = useState(false);
  const [streamingSession, setStreamingSession] = useState(null);
  // Removed keepGamingData state - feature removed
  const [lastCleanup, setLastCleanup] = useState(null);
  const [vpnConnectionInfo, setVpnConnectionInfo] = useState(null);
  const [gpuReady, setGpuReady] = useState(false);
  const [launchingGPUId, setLaunchingGPUId] = useState(null);
  // GPU offer filter / sort state. Persisted to localStorage so a user's choice
  // survives reloads without needing a server round-trip.
  const [gpuRegion, setGpuRegion] = useState(() => localStorage.getItem('levelup.gpuRegion.v2') || 'any');
  const [gpuUserRegion, setGpuUserRegion] = useState(() => localStorage.getItem('levelup.gpuUserRegion') || 'EU');
  const [gpuSortBy, setGpuSortBy] = useState(() => localStorage.getItem('levelup.gpuSortBy') || 'price');
  useEffect(() => { localStorage.setItem('levelup.gpuRegion.v2', gpuRegion); }, [gpuRegion]);
  useEffect(() => { localStorage.setItem('levelup.gpuUserRegion', gpuUserRegion); }, [gpuUserRegion]);
  useEffect(() => { localStorage.setItem('levelup.gpuSortBy', gpuSortBy); }, [gpuSortBy]);
  const { data: userStats } = useQuery(
    'userStats',
    usersAPI.getUserStats,
    { refetchInterval: 10000 }
  );
  const walletMkd = Number(userStats?.play_credit ?? userStats?.wallet_balance ?? 0);
  const { data: lemonSub } = useQuery(
    'lemonSubscription',
    paymentsAPI.getLemonSubscription,
    {
      enabled: isGame && !user?.is_admin,
      refetchInterval: 30000,
    }
  );
  const familyQuad = Boolean(user?.family_quad || user?.family_id);
  const billingUserId = user?.family_id || user?.id;
  const canLaunchGame = Boolean(user?.is_admin || lemonSub?.active);

  const { data: gamingSessions, refetch: refetchSessions, isLoading: sessionsLoading, isFetched: sessionsFetched } = useQuery(
    'gamingSessions',
    gamingAPI.getSessions,
    {
      refetchInterval: 3000,
      keepPreviousData: true,
      retry: 1,
      onError: (error) => {
        console.error('Failed to fetch gaming sessions:', error);
      },
    }
  );


  // Monitor VM startup progress - moved after activeSession definition


  const createSessionMutation = useMutation(gamingAPI.createSession, {
    onSuccess: (data) => {
      // Clear loading state
      setLaunchingGPUId(null);
      // Refresh sessions to get the new session
      refetchSessions();
      // Show success message
      showSuccess(
        isGame ? 'Game Session Launched' : 'Lab Session Launched',
        isGame
          ? 'Your VM gaming session is being created. The game stack boots inside the VM (may take a few minutes).'
          : 'Your Docker Lab session is being created.'
      );
      // Redirect to session details after a short delay
      setTimeout(() => {
        // The session details will be shown automatically when activeSession updates
        // No need for manual redirection as the VMStatusDashboard will appear
      }, 1000);
    },
    onError: (error) => {
      const status = error.response?.status;
      const timedOut =
        status === 502 ||
        status === 504 ||
        !error.response ||
        error.code === 'ECONNABORTED' ||
        /timeout/i.test(String(error.message || ''));
      if (timedOut) {
        // Vast create often outlives the HTTP gateway. The session row
        // is already claimed — keep the boot UI and do not flash an error.
        refetchSessions();
        return;
      }
      // Handle different error response formats
      let errorMessage = 'Failed to create gaming VM';
      let errorTitle = 'VM Creation Failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
            // Check if it's a "one GPU per user" error
            if (errorMessage.includes('already have an active gaming session')) {
              errorTitle = 'Multiple GPU Instances Not Allowed';
            }
            if (status === 402) {
              errorTitle = 'Subscription required';
            }
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
          }
        }
      }
      
      setLaunchingGPUId(null);
      showError(errorTitle, errorMessage);
      refetchSessions();
      console.error('VM creation error:', error);
    },
  });

  const stopSessionMutation = useMutation(gamingAPI.stopSession, {
    onSuccess: (data) => {
      refetchSessions();
      setGpuReady(false);
      showSuccess(
        data?.destroying ? 'Destroy started' : 'Instance Destroyed',
        data?.message || 'GPU destroyed. Billing for this machine should stop shortly.'
      );
    },
    onError: (error) => {
      let errorTitle = 'Destroy Failed';
      let errorMessage = 'Failed to destroy the GPU';
      const status = error.response?.status;
      if (status === 502 || status === 504) {
        refetchSessions();
        errorTitle = 'Destroy is still running';
        errorMessage =
          'The page waited too long, but destroy may still be running in the background (Steam copy to the NAS can take a few minutes). Refresh in a minute.';
      } else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          // nginx HTML error pages — don't dump raw HTML into the modal
          errorMessage = error.response.data.includes('<html')
            ? `Server error (${status || 'unknown'}). Refresh and try Cleanup Orphaned, or destroy the GPU again.`
            : error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      showError(errorTitle, errorMessage);
    },
  });

  const activeSession = gamingSessions?.sessions?.find(session => 
    ['pending', 'starting', 'running', 'stopping'].includes(session.status)
  );
  const launchInProgress = createSessionMutation.isLoading || !!launchingGPUId;

  const { data: availableGPUs, isLoading: gpusLoading, refetch: refetchGPUs } = useQuery(
    ['availableGPUs', mode, gpuRegion, gpuUserRegion, gpuSortBy, familyQuad],
    () => gpuAPI.getAvailableGPUs(undefined, mode, {
      region: gpuRegion,
      userRegion: gpuUserRegion,
      sortBy: gpuSortBy,
      hideMismatch: true,
      familyQuad,
    }),
    {
      // Do not hammer Vast while a machine is already up — that poll blocks the
      // API and flashes "Failed to Load GPUs / Sessions".
      enabled: sessionsFetched && !activeSession && !launchInProgress,
      retry: 1,
      keepPreviousData: true,
      refetchInterval: 60000,
      refetchIntervalInBackground: false,
      onError: (error) => {
        console.log('GPUs query failed:', error);
      },
    }
  );

  useEffect(() => {
    if (activeSession) {
      setLaunchingGPUId(null);
    }
  }, [activeSession]);

  // Debug logging
  useEffect(() => {
    console.log('Gaming sessions data:', gamingSessions);
    console.log('Active session:', activeSession);
  }, [gamingSessions, activeSession]);

  // Monitor VM startup progress
  useEffect(() => {
    if (activeSession && activeSession.status === 'running') {
      // VM is ready - we'll show this as inline info instead of dialog
      console.log('VM is ready for connection');
    }
  }, [activeSession]);

  // Handler for launching session from GPU tile
  const handleLaunchSessionFromGPU = (gpu) => {
    console.log('🚀 Launching GPU:', gpu.id, gpu.gpu_type);
    
    // Prevent multiple rapid submissions
    if (createSessionMutation.isLoading || launchingGPUId) {
      console.log('⚠️ Already launching, ignoring request');
      return;
    }
    if (activeSession) {
      showError(
        'Multiple GPU Instances Not Allowed',
        familyQuad
          ? 'Stop the current machine before launching another. One GPU per family.'
          : 'Stop the current machine before launching another. One GPU per account.'
      );
      return;
    }
    
    if (!sessionName.trim()) {
      showError('Session Name Required', 'Please enter a session name');
      return;
    }
    if (isGame && !user?.is_admin) {
      if (!lemonSub) {
        showError('Just a moment', 'Checking subscription status.');
        return;
      }
      if (!lemonSub.active) {
        showError(
          'Subscription required',
          'Subscribe on your Profile page to launch a Game GPU.'
        );
        return;
      }
    }
    
    console.log('🎯 Setting loading state for GPU:', gpu.id);
    // Set loading state for this specific GPU
    setLaunchingGPUId(gpu.id);
    
    // Launch session with the selected GPU
    createSessionMutation.mutate({
      session_name: sessionName,
      game_title: gameTitle || null,
      gpu_type: gpu.gpu_type,
      max_price_per_hour: 10.0, // Set a reasonable default max price
      gpu_id: gpu.id,
      disk_space: gpu.disk_space ?? gpu.specs?.billed_disk_gb ?? gpu.specs?.disk_space,
      instance_mode: mode,
      play_backend: isGame ? 'wolf_webrtc' : undefined,
      quoted_usd_per_hour: gpu.quoted_usd_per_hour
        || gpu.specs?.usd_total_per_hour
        || gpu.specs?.usd_price_per_hour
        || 0,
      gpu_ram: gpu.specs?.gpu_ram || gpu.gpu_ram || 0,
    });
  };

  // Removed handleCreateSession - using handleLaunchSessionFromGPU directly

  const resolveInstanceId = (session) =>
    session?.instance_id ||
    session?.vast_instance_id ||
    session?.vm_details?.instance_id ||
    (session?.id && !String(session.id).startsWith('session-') ? session.id : null);

  const bootId = activeSession?.db_id || resolveInstanceId(activeSession);
  const { data: bootStatus } = useQuery(
    ['bootProgress', bootId],
    () => fetchBootProgress(bootId),
    {
      enabled: !!bootId,
      staleTime: 0,
      keepPreviousData: true,
      refetchInterval: (latest) =>
        latest?.failed || (latest?.ready && latest?.tailscale_ready) ? 8000 : 2000,
      refetchIntervalInBackground: true,
    }
  );
  const bootFailShown = useRef(false);
  useEffect(() => {
    if (!bootStatus?.failed || bootFailShown.current) return;
    bootFailShown.current = true;
    showError(
      'GPU did not start',
      bootStatus.detail
        || 'The rented machine failed before Airpulse could start. We closed it so you can pick another GPU.'
    );
    refetchSessions();
  }, [bootStatus, showError, refetchSessions]);
  useEffect(() => {
    if (!activeSession) bootFailShown.current = false;
  }, [activeSession]);

  const handleStopSession = (session = activeSession) => {
    const target = session || activeSession;
    if (!target) {
      showError('No Active Session', 'No active session to destroy');
      return;
    }

    const instanceId = resolveInstanceId(target);
    if (!instanceId) {
      showError('Session Error', 'No GPU instance ID found for this session');
      return;
    }

    if (String(target.status || '') === 'stopping') {
      stopSessionMutation.mutate(String(instanceId));
      return;
    }

    const confirmed = window.confirm(
      `Destroy GPU ${instanceId}?\n\nThis stops billing and cannot be undone. You will need to launch a new GPU to play again.`
    );
    if (!confirmed) return;

    stopSessionMutation.mutate(String(instanceId));
  };

  // const forceStopSessionMutation = useMutation(
  //   (instanceId) => fetch(`/api/v1/vm-gaming/sessions/${instanceId}/force-stop`, {
  //     method: 'POST',
  //     headers: {
  //       'Authorization': `Bearer ${localStorage.getItem('token')}`,
  //       'Content-Type': 'application/json'
  //     }
  //   }).then(res => res.json()),
  //   {
  //     onSuccess: (data) => {
  //       refetchSessions();
  //       toast.success('Session force stopped and marked as orphaned');
  //     },
  //     onError: (error) => {
  //       toast.error('Failed to force stop session');
  //       console.error('Force stop error:', error);
  //     }
  //   }
  // );

  // const handleForceStopSession = () => {
  //   if (activeSession) {
  //     const instanceId = activeSession.instance_id || 
  //                       activeSession.vm_details?.instance_id || 
  //                       activeSession.id;
  //     if (instanceId) {
  //       forceStopSessionMutation.mutate(instanceId);
  //     }
  //   }
  // };

  const cleanupMutation = useMutation(gamingAPI.cleanupOrphaned, {
    onSuccess: (data) => {
      refetchSessions(); // Refresh sessions from backend
      setLastCleanup(new Date().toLocaleTimeString());
      showSuccess('Cleanup Complete', `Cleaned up ${data.cleaned_count} orphaned sessions`);
    },
    onError: (error) => {
      showError('Cleanup Failed', 'Failed to cleanup orphaned sessions');
    },
  });

  const handleCleanupOrphaned = () => {
    console.log('Cleanup orphaned clicked');
    cleanupMutation.mutate();
  };

  // Removed resumeSessionMutation and handleResumeSession - pause/resume functionality removed

  if (gpusLoading || !sessionsFetched || (sessionsLoading && !gamingSessions)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  // Show authentication required message if not logged in
  if (!user) {
    return (
      <div className="space-y-4 sm:space-y-6 lg:space-y-8 p-2 sm:p-4 lg:p-6">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{pageTitle}</h1>
          <p className="text-sm sm:text-base text-white text-opacity-80">
            {pageSubtitle}
          </p>
        </div>
        
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6 text-center">
          <h3 className="text-yellow-400 font-semibold text-lg mb-2">Authentication Required</h3>
          <p className="text-white text-opacity-80 mb-4">
            Please log in to access sessions and create new ones.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 p-2 sm:p-4 lg:p-6">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{pageTitle}</h1>
        <p className="text-sm sm:text-base text-white text-opacity-80">
          {pageSubtitle}
        </p>
        {!isGame && (
          <p className="mt-2 text-xs text-amber-200/80 max-w-2xl mx-auto">
            Lab keeps the Docker-instance path for future private / personal AI images.
            Playable Moonlight streaming uses the Game tab.
          </p>
        )}
        {isGame && familyQuad && (
          <div className="mt-4 mx-auto max-w-3xl text-left rounded-lg border border-sky-400/40 bg-sky-950/40 p-4 space-y-2">
            <p className="text-white font-semibold text-sm">How four people play on this GPU</p>
            <ul className="text-sm text-white/80 space-y-1.5 list-disc pl-5">
              <li>
                <span className="text-white font-medium">Moonlight app (PC / TV):</span> up to four
                people at the same time. Each installs Tailscale + Moonlight and pairs with the 100.x IP.
              </li>
              <li>
                <span className="text-white font-medium">Browser:</span> only one person at a time.
                Extra browser tabs share the same stream — they do not start three more games.
              </li>
              <li>
                <span className="text-white font-medium">Mix:</span> one in the browser + three in
                Moonlight is the full house. If a fourth person needs in, the browser player switches to Moonlight.
              </li>
            </ul>
            <p className="text-xs text-white/55">
              This is one 12 GB 4070-class graphics card, not four GeForce NOW machines. Heavy games on all four
              seats will hitch; local co-op or lighter titles work best.
            </p>
            {(userStats?.family_members || []).length > 0 && (
              <ul className="text-xs text-sky-100/80 space-y-0.5 pt-1">
                {userStats.family_members.map((m) => (
                  <li key={m.username}>
                    Seat {m.family_seat}: {m.username}
                    {m.family_master ? ' (master)' : ''}
                    {m.you ? ' — you' : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {isGame && !familyQuad && <PlayLobbyPanel />}
        {isGame && !user?.is_admin && lemonSub && !canLaunchGame && (
          <p className="mt-3 text-sm text-amber-200 max-w-2xl mx-auto">
            An Airpulse Game monthly subscription is required to launch a GPU.{' '}
            <a href="/profile" className="underline">Subscribe on Profile</a>.
          </p>
        )}
      </div>

      {/* Active Session - Two Column Layout */}
      {(activeSession || launchInProgress) && (
        <div className="grid grid-cols-1 gap-6">
          {/* Left Column: Machine Ready & Connect */}
          <div className="space-y-4">
            {!activeSession && launchInProgress && (
              <div className="rounded-lg border border-amber-500/50 bg-amber-950/40 p-4 text-amber-100">
                <p className="font-semibold">Launching one GPU — extra clicks are blocked</p>
                <p className="text-sm text-amber-100/80 mt-1">
                  One machine per account. Wait for this session to appear or destroy it before launching another.
                </p>
              </div>
            )}
            {/* VM Status Dashboard */}
            {activeSession && (
              <VMStatusDashboard 
                session={activeSession}
                onStatusUpdate={(status) => {
                  // Set gpuReady when status is 'ready' (which maps from Vast.ai "success")
                  setGpuReady(status.is_ready || status.status === 'ready');
                }}
              />
            )}

            {activeSession && isGame && (
              <div className="mt-4 space-y-4">
                <GameConnectPanel
                  sessionId={resolveInstanceId(activeSession) || (activeSession.db_id != null ? String(activeSession.db_id) : null)}
                  session={activeSession}
                />
                <IdleDestroyToggle userId={billingUserId} session={activeSession} />
                <SteamPersistToggle userId={billingUserId} />
              </div>
            )}

            {/* Destroy running Vast instance */}
            {activeSession && (
              <div className="rounded-lg border border-red-500/40 bg-red-950/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-white font-semibold">Stop / destroy GPU</p>
                  <p className="text-sm text-white/70">
                    Ends billing on GPU{' '}
                    <span className="font-mono text-red-200">
                      {resolveInstanceId(activeSession) || 'unknown'}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleStopSession(activeSession)}
                  disabled={stopSessionMutation.isLoading || activeSession.status === 'stopping'}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 transition-colors"
                >
                  <Square className="h-4 w-4 fill-current" />
                  {stopSessionMutation.isLoading || activeSession.status === 'stopping' ? 'Destroying…' : 'Destroy Instance'}
                </button>
              </div>
            )}
            
            {/* Session History */}
            <div className="gaming-card rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white">Session History</h3>
                  {lastCleanup && (
                    <p className="text-sm text-gray-400">Last cleanup: {lastCleanup}</p>
                  )}
                </div>
                <button
                  onClick={handleCleanupOrphaned}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <span>🧹</span>
                  <span>Cleanup Orphaned</span>
                </button>
              </div>
              {gamingSessions?.sessions && gamingSessions.sessions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-white">
                    <thead>
                      <tr className="border-b border-white border-opacity-20">
                        <th className="text-left py-3 px-4">Session</th>
                        <th className="text-left py-3 px-4">Game</th>
                        <th className="text-left py-3 px-4">GPU</th>
                        <th className="text-left py-3 px-4">Time</th>
                        <th className="text-left py-3 px-4">Spent</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gamingSessions.sessions.map((session) => {
                        const isActive = ['pending', 'starting', 'running', 'stopping'].includes(session.status);
                        return (
                        <tr key={session.id} className="border-b border-white border-opacity-10">
                          <td className="py-3 px-4">{session.session_name}</td>
                          <td className="py-3 px-4">{session.game_title || '-'}</td>
                          <td className="py-3 px-4">{session.gpu_type}</td>
                          <td className="py-3 px-4">
                            {formatSessionHours(session.total_hours)}
                            {isActive ? (
                              <span className="block text-xs text-gray-400">from GPU start</span>
                            ) : null}
                          </td>
                          <td className="py-3 px-4">
                            <div>{formatSessionCost(session.share_cost ?? session.total_cost, session.share_rate_mkd || session.gpu_price_per_hour)}</div>
                            <div className="text-xs text-gray-400">
                              {Number(session.share_rate_mkd || session.gpu_price_per_hour) > 0
                                ? `${Number(session.share_rate_mkd || session.gpu_price_per_hour).toFixed(2)} MKD/h`
                                : 'rate pending'}
                            </div>
                            {session.billing_note ? (
                              <div className="text-[11px] text-indigo-200/80 mt-0.5 leading-tight">
                                {session.billing_note}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              session.status === 'completed' ? 'bg-green-500' :
                              session.status === 'running' ? 'bg-blue-500' :
                              session.status === 'stopping' ? 'bg-amber-500' :
                              session.status === 'failed' ? 'bg-red-600' :
                              session.status === 'stopped' ? 'bg-red-500' :
                              'bg-gray-500'
                            }`}>
                              {session.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {isActive ? (
                              <button
                                type="button"
                                onClick={() => handleStopSession(session)}
                                disabled={stopSessionMutation.isLoading || session.status === 'stopping'}
                                className="text-sm px-3 py-1 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white"
                              >
                                {session.status === 'stopping' ? 'Destroying…' : 'Destroy'}
                              </button>
                            ) : (
                              <span className="text-gray-500 text-sm">—</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No sessions yet. Create a new session to get started.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create New Session */}
      {!activeSession && !launchInProgress && (
        <div className="gaming-card rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Create New Session</h3>
            <div className="mb-4 space-y-4">
              <IdleDestroyToggle userId={user?.id} session={null} />
              <SteamPersistToggle userId={user?.id} />
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Session Name
                </label>
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => {
                    console.log('🎮 Gaming: sessionName changed to:', e.target.value);
                    setSessionName(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="My Gaming Session"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Game Title (Optional)
                </label>
                <input
                  type="text"
                  value={gameTitle}
                  onChange={(e) => setGameTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Cyberpunk 2077"
                />
              </div>

              {/* GPU Selection - Only show when session name is provided */}
              {console.log('🎮 Gaming: sessionName:', sessionName, 'trimmed:', sessionName.trim(), 'should show GPUs:', !!sessionName.trim())}
              {sessionName && sessionName.trim() && sessionName.trim().length > 0 && sessionName !== '' ? (
                <div>
                  <label className="block text-sm font-medium text-white mb-4">
                    Select GPU Instance
                  </label>
                  <GPUSelection
                    availableGPUs={availableGPUs || []}
                    onLaunchSession={handleLaunchSessionFromGPU}
                    isLoading={gpusLoading}
                    isLaunching={createSessionMutation.isLoading}
                    launchingGPUId={launchingGPUId}
                    region={gpuRegion}
                    userRegion={gpuUserRegion}
                    sortBy={gpuSortBy}
                    instanceMode={mode}
                    onRegionChange={setGpuRegion}
                    onUserRegionChange={setGpuUserRegion}
                    onSortByChange={setGpuSortBy}
                    onRefresh={refetchGPUs}
                    walletMkd={walletMkd}
                    creditGated={isGame && !user?.is_admin}
                    familyQuad={familyQuad}
                  />
                </div>
              ) : (
                /* Show message when session name is not provided */
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-blue-400 text-lg">💡</span>
                    <span className="text-blue-400 font-semibold">Enter Session Name to Continue</span>
                  </div>
                  <p className="text-blue-300 text-sm">
                    Please enter a session name above to see available GPU instances and start your gaming session.
                  </p>
                </div>
              )}

            </form>
        </div>
      )}

      {/* Removed duplicate Session History - now in left column */}

      {/* Streaming Interface Modal */}
      {showStreamingInterface && streamingSession && (
        <StreamingInterface
          session={streamingSession}
          onClose={() => {
            setShowStreamingInterface(false);
            setStreamingSession(null);
          }}
        />
      )}

      {/* Automated Gaming Modal */}
      {showAutomatedGaming && streamingSession && (
        <AutomatedGaming
          session={streamingSession}
          onClose={() => {
            setShowAutomatedGaming(false);
            setStreamingSession(null);
          }}
        />
      )}
    </div>
  );
};

export default Gaming;
